import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, Search, X, Users, AlertTriangle, Inbox, Flag, CloudUpload } from 'lucide-react';
import { toast } from 'sonner';
import {
  fetchRoutingMeta,
  fetchRoutingSummary,
  listRoutingLeads,
  getRoutingLead,
  updateRoutingLead,
  reassignRoutingLead,
  fetchRoster,
  syncRoutingSheet,
  setRoutingLeadUrl,
} from '../lib/routingService';
import { formatApiError } from '../lib/campaignService';

const DEFAULT_STATUSES = ['New', 'Contacted', 'Follow-up', 'Meeting scheduled', 'Won', 'Lost', 'Invalid'];
const CLOSED = ['Won', 'Lost', 'Invalid'];
const PAGE_SIZE = 50;

function formatIst(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
  });
}

function slaState(lead, now) {
  if (lead.flagged) return { label: 'Flagged 24h+', tone: 'bad' };
  if (lead.status === 'New' && !lead.historical && lead.reminder_due_at
    && new Date(lead.reminder_due_at).getTime() <= now) {
    return { label: 'Overdue', tone: 'warn' };
  }
  return null;
}

function sheetLabel(lead) {
  if (lead.sheet_sync === 'synced') return 'In sheet';
  if (lead.sheet_sync === 'pending') return 'Waiting for sheet';
  if (lead.sheet_sync === 'dashboard_only') return 'Dashboard only';
  return '—';
}

function sheetModeLabel(meta) {
  if (!meta) return '';
  if (!meta.sheet_configured) return 'Google Sheet not connected yet';
  if (meta.can_write) return 'Sheet connected — status is written back every 5 minutes';
  return 'Sheet is read-only — share it as Editor to write status back';
}

function SummaryCard({ label, value, icon: Icon, active, onClick }) {
  return (
    <button type="button" className={`stat-card clickable${active ? ' active' : ''}`} onClick={onClick}>
      <div className="stat-info">
        <span className="stat-number">{value ?? 0}</span>
        <span className="stat-title">{Icon && <Icon size={14} />} {label}</span>
      </div>
    </button>
  );
}

function LeadPanel({ apiUrl, lead, roster, user, statuses, onClose, onChanged }) {
  const [status, setStatus] = useState(lead.status);
  const [reason, setReason] = useState(lead.status_reason || '');
  const [followUp, setFollowUp] = useState(lead.next_follow_up_date || '');
  const [meeting, setMeeting] = useState(lead.meeting_date || '');
  const [notes, setNotes] = useState(lead.clara_follow_up_notes || '');
  const [assignee, setAssignee] = useState('');
  const [city, setCity] = useState('');
  const [busy, setBusy] = useState(false);

  const canReassign = user?.role === 'admin' || user?.role === 'manager';
  const closed = CLOSED.includes(lead.status);
  const needsReason = status === 'Lost' || status === 'Invalid';
  const needsFollowUp = status === 'Follow-up';
  const needsMeeting = status === 'Meeting scheduled';
  // Mirrors the server: a manager may hand a lead to someone in the lead's zone, in their own zone,
  // or who reports to them. Offering anyone else only ends in an error.
  const me = roster.find((p) => p.id === user?.person_id);
  const candidates = roster.filter((p) => {
    if (!p.receives_leads || !p.active) return false;
    if (user?.role !== 'manager') return true;
    return p.zone === lead.zone || (me?.zone && p.zone === me.zone) || p.reports_to_id === user?.person_id;
  });
  const unassigned = lead.queue === 'unassigned';

  const save = async () => {
    if (needsReason && !reason.trim()) { toast.error(`${status} needs a reason`); return; }
    if (needsFollowUp && !followUp) { toast.error('Pick the next follow-up date'); return; }
    if (needsMeeting && !meeting) { toast.error('Pick the meeting date'); return; }
    setBusy(true);
    try {
      const updated = await updateRoutingLead(apiUrl, lead.id, {
        status,
        reason: reason.trim() || null,
        next_follow_up_date: followUp || null,
        meeting_date: meeting || null,
        clara_follow_up_notes: notes,
      });
      toast.success('Lead updated');
      onChanged(updated);
    } catch (err) {
      toast.error(formatApiError(err, 'Could not update the lead'));
    } finally {
      setBusy(false);
    }
  };

  const reassign = async () => {
    if (!assignee) { toast.error('Choose who should take this lead'); return; }
    setBusy(true);
    try {
      const updated = await reassignRoutingLead(apiUrl, lead.id, { assignee_id: assignee, city: city.trim() || null });
      toast.success('Lead reassigned');
      setAssignee('');
      setCity('');
      onChanged(updated);
    } catch (err) {
      toast.error(formatApiError(err, 'Could not reassign the lead'));
    } finally {
      setBusy(false);
    }
  };

  const history = [...(lead.history || [])].reverse();

  return (
    <aside className="routing-panel" aria-label="Lead details">
      <div className="routing-panel-head">
        <div>
          <h3>{lead.name || 'Unnamed enquiry'}</h3>
          <p className="routing-sub">
            {lead.phone} · {lead.city || lead.city_raw || 'No city'}{lead.state ? `, ${lead.state}` : ''}
          </p>
          {lead.phone && (
            <div className="routing-quick">
              <a className="campaign-secondary-btn" href={`tel:${lead.phone}`}>Call</a>
              {lead.whatsapp_ready !== false && (
                <a className="campaign-secondary-btn" href={`https://wa.me/${String(lead.phone).replace(/\D/g, '')}`} target="_blank" rel="noreferrer">WhatsApp</a>
              )}
            </div>
          )}
        </div>
        <button type="button" className="refresh-btn" onClick={onClose} aria-label="Close"><X size={18} /></button>
      </div>

      <dl className="routing-facts">
        <div><dt>Status</dt><dd><span className={`campaign-status-badge status-${(lead.status || '').toLowerCase().replace(/\s+/g, '-')}`}>{lead.status}</span></dd></div>
        <div><dt>Assigned to</dt><dd>{lead.assigned_to_name || 'Nobody yet (Unassigned queue)'}</dd></div>
        <div><dt>Zone</dt><dd>{lead.zone}</dd></div>
        <div><dt>Source</dt><dd>{lead.source}</dd></div>
        <div><dt>Enquiry</dt><dd>{formatIst(lead.enquiry_at)} IST</dd></div>
        <div><dt>First contact</dt><dd>{lead.first_contacted_at ? `${formatIst(lead.first_contacted_at)} IST` : 'Not yet'}</dd></div>
        <div><dt>Product</dt><dd>{lead.product_interest || '—'}</dd></div>
        <div><dt>Occupation</dt><dd>{lead.occupation || '—'}</dd></div>
        {lead.email && <div><dt>Email</dt><dd>{lead.email}</dd></div>}
        {user?.role === 'admin' && <div><dt>Google Sheet</dt><dd>{sheetLabel(lead)}</dd></div>}
      </dl>

      {(lead.remarks || lead.follow_up_1 || lead.follow_up_2 || lead.follow_up_3) && (
        <div className="routing-block">
          <h4>Notes from Haique&apos;s sheet</h4>
          {[lead.remarks, lead.follow_up_1, lead.follow_up_2, lead.follow_up_3].filter(Boolean).map((t) => <p key={t}>{t}</p>)}
        </div>
      )}

      {user?.role === 'staff' ? (
        <p className="campaign-manager-hint">View only. Your login can see this zone&apos;s leads but not change them.</p>
      ) : (
      <div className="routing-block">
        <h4>Update status</h4>
        {closed && user?.role === 'salesperson' && (
          <p className="campaign-manager-hint">This lead is closed. Only a manager or admin can reopen it.</p>
        )}
        <label>Status
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        {needsReason && (
          <label>Reason (required)
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. price, timing, competitor, wrong number" />
          </label>
        )}
        {(needsFollowUp || followUp) && (
          <label>Next follow-up date{needsFollowUp ? ' (required)' : ''}
            <input type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} />
          </label>
        )}
        {needsMeeting && (
          <label>Meeting date (required)
            <input type="date" value={meeting} onChange={(e) => setMeeting(e.target.value)} />
          </label>
        )}
        <label>Follow-up notes
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add a note about this customer (the team can see it)" />
        </label>
        <button type="button" className="campaign-primary-btn" onClick={save} disabled={busy}>
          {busy ? 'Saving…' : 'Save status'}
        </button>
      </div>
      )}

      {canReassign && (
        <div className="routing-block">
          <h4>{unassigned ? 'Assign this lead' : 'Reassign'}</h4>
          <label>Person
            <select value={assignee} onChange={(e) => setAssignee(e.target.value)}>
              <option value="">Choose…</option>
              {candidates.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.zone})</option>)}
            </select>
          </label>
          {unassigned && (
            <label>City, if the system could not read it
              <input value={city} onChange={(e) => setCity(e.target.value)} placeholder={lead.city_raw ? `Customer typed: ${lead.city_raw}` : 'e.g. Jaipur'} />
              <small>The system remembers this spelling for next time.</small>
            </label>
          )}
          <button type="button" className="campaign-secondary-btn" onClick={reassign} disabled={busy}>
            {busy ? 'Working…' : (unassigned ? 'Assign' : 'Reassign')}
          </button>
        </div>
      )}

      {(lead.duplicates || []).length > 0 && (
        <div className="routing-block">
          <h4>Repeat enquiries ({lead.duplicates.length})</h4>
          <ul className="routing-list">
            {lead.duplicates.map((d) => (
              <li key={`${d.sheet_key}-${d.at}`}>{formatIst(d.at)} · {d.source} · {d.city_raw || 'no city'}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="routing-block">
        <h4>History</h4>
        <ul className="routing-list">
          {history.slice(0, 25).map((h, i) => (
            <li key={`${h.at}-${i}`}>{formatIst(h.at)} · {String(h.kind).replace(/_/g, ' ')}</li>
          ))}
          {history.length === 0 && <li>Nothing yet</li>}
        </ul>
      </div>
    </aside>
  );
}

export default function RoutingTab({ apiUrl, user, initialLeadId, onInitialHandled }) {
  const [meta, setMeta] = useState(null);
  const [summary, setSummary] = useState(null);
  const [leads, setLeads] = useState({ items: [], total: 0 });
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({ status: '', zone: '', queue: '', search: '', overdue: false, flagged: false });
  const [error, setError] = useState('');

  const statuses = meta?.statuses?.length ? meta.statuses : DEFAULT_STATUSES;
  const now = Date.now();

  const query = useMemo(() => ({
    status: filters.status,
    zone: filters.zone,
    queue: filters.queue,
    search: filters.search.trim(),
    overdue: filters.overdue ? true : '',
    flagged: filters.flagged ? true : '',
    skip: page * PAGE_SIZE,
    limit: PAGE_SIZE,
  }), [filters, page]);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const [m, s, l] = await Promise.all([
        fetchRoutingMeta(apiUrl),
        fetchRoutingSummary(apiUrl),
        listRoutingLeads(apiUrl, query),
      ]);
      setMeta(m);
      setSummary(s);
      setLeads(l || { items: [], total: 0 });
      setError('');
    } catch (err) {
      setError(formatApiError(err, 'Could not load leads'));
    } finally {
      setLoading(false);
    }
  }, [apiUrl, query]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const id = setInterval(() => load(true), 60000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    fetchRoster(apiUrl).then((r) => setRoster(r?.items || [])).catch(() => setRoster([]));
  }, [apiUrl]);

  const openLead = useCallback(async (leadId) => {
    try {
      const lead = await getRoutingLead(apiUrl, leadId);
      setSelected(lead);
      setRoutingLeadUrl(lead.id);
    } catch (err) {
      toast.error(formatApiError(err, 'That lead is not available to you'));
      setRoutingLeadUrl('');
    }
  }, [apiUrl]);

  useEffect(() => {
    if (!initialLeadId) return;
    openLead(initialLeadId).finally(() => {
      if (onInitialHandled) onInitialHandled();
    });
  }, [initialLeadId, openLead, onInitialHandled]);

  const closePanel = () => {
    setSelected(null);
    setRoutingLeadUrl('');
  };

  const handleChanged = (updated) => {
    setSelected(updated);
    setLeads((prev) => ({ ...prev, items: prev.items.map((l) => (l.id === updated.id ? updated : l)) }));
    load(true);
  };

  const setFilter = (patch) => {
    setPage(0);
    setFilters((f) => ({ ...f, ...patch }));
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const result = await syncRoutingSheet(apiUrl);
      toast.success(`Synced: ${result?.created ?? 0} new, ${result?.duplicates ?? 0} repeat enquiries`);
      await load(true);
    } catch (err) {
      toast.error(formatApiError(err, 'Sync failed'));
    } finally {
      setSyncing(false);
    }
  };

  const zones = meta?.zones || {};
  const pages = Math.max(1, Math.ceil((leads.total || 0) / PAGE_SIZE));

  return (
    <div className="routing-page">
      <div className="section-header">
        <div>
          <h2><Users size={22} /> Lead Routing</h2>
          <p className="campaign-detail-sub">
            Meta and WhatsApp enquiries, routed to the right salesperson by city.
            {meta && user?.role === 'admin' && <span className="routing-sheet-mode"> {sheetModeLabel(meta)}.</span>}
          </p>
        </div>
        <div className="section-header-actions">
          {user?.role === 'admin' && meta?.sheet_configured && (
            <button type="button" className="campaign-secondary-btn" onClick={syncNow} disabled={syncing}>
              <CloudUpload size={16} /> {syncing ? 'Syncing…' : 'Sync now'}
            </button>
          )}
          <button type="button" className="refresh-btn" onClick={() => load()} aria-label="Refresh"><RefreshCw size={18} /></button>
        </div>
      </div>

      {error && <div className="routing-error">{error}</div>}

      <div className="stats-grid campaign-stats-grid routing-stats">
        <SummaryCard label="All leads" value={summary?.total} icon={Inbox}
          active={!filters.status && !filters.queue && !filters.overdue && !filters.flagged}
          onClick={() => setFilter({ status: '', queue: '', overdue: false, flagged: false })} />
        <SummaryCard label="New" value={summary?.new} active={filters.status === 'New'}
          onClick={() => setFilter({ status: filters.status === 'New' ? '' : 'New', queue: '', overdue: false, flagged: false })} />
        <SummaryCard label="Overdue for first contact" value={summary?.overdue} icon={AlertTriangle} active={filters.overdue}
          onClick={() => setFilter({ overdue: !filters.overdue, status: '', queue: '', flagged: false })} />
        <SummaryCard label="Unassigned" value={summary?.unassigned} active={filters.queue === 'unassigned'}
          onClick={() => setFilter({ queue: filters.queue === 'unassigned' ? '' : 'unassigned', status: '', overdue: false, flagged: false })} />
        <SummaryCard label="Flagged 24h+" value={summary?.flagged} icon={Flag} active={filters.flagged}
          onClick={() => setFilter({ flagged: !filters.flagged, status: '', queue: '', overdue: false })} />
      </div>

      {summary?.zones && Object.keys(summary.zones).length > 0 && (
        <div className="campaign-messages-table-wrap routing-zones-wrap">
          <table className="campaign-messages-table routing-zones">
            <thead><tr><th>Zone</th><th>Leads</th><th>New</th><th>Overdue</th><th>Unassigned</th><th>Flagged</th></tr></thead>
            <tbody>
              {Object.entries(summary.zones).filter(([zone]) => zone !== 'UNASSIGNED' || user?.role === 'admin').map(([zone, c]) => (
                <tr key={zone} className="routing-zone-row" onClick={() => setFilter({ zone: filters.zone === zone ? '' : zone })}>
                  <td>{zones[zone] || zone}{filters.zone === zone ? ' ✓' : ''}</td>
                  <td>{c.total}</td><td>{c.new}</td><td>{c.overdue}</td><td>{c.unassigned}</td><td>{c.flagged}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="routing-filters">
        <label className="routing-search">
          <Search size={16} />
          <input value={filters.search} onChange={(e) => setFilter({ search: e.target.value })} placeholder="Search name, phone or city" />
        </label>
        <select value={filters.status} onChange={(e) => setFilter({ status: e.target.value })} aria-label="Status">
          <option value="">Any status</option>
          {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.zone} onChange={(e) => setFilter({ zone: e.target.value })} aria-label="Zone">
          <option value="">Any zone</option>
          {Object.entries(zones).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
        </select>
      </div>

      <div className={`routing-layout${selected ? ' with-panel' : ''}`}>
        <div className="campaign-messages-table-wrap">
          <table className="campaign-messages-table routing-table">
            <thead>
              <tr><th>Enquiry</th><th>Customer</th><th>City</th><th>Zone</th><th>Assigned to</th><th className="col-extra">Source</th><th>Status</th><th>Follow-up</th>{user?.role === 'admin' && <th className="col-extra">Sheet</th>}</tr>
            </thead>
            <tbody>
              {loading && leads.items.length === 0 ? (
                <tr><td colSpan={9} className="campaign-empty-row">Loading leads…</td></tr>
              ) : leads.items.length === 0 ? (
                <tr><td colSpan={9} className="campaign-empty-row">{filters.search || filters.status || filters.zone || filters.queue || filters.overdue || filters.flagged ? 'No leads match these filters. Clear a filter to see more.' : 'No leads yet. New enquiries appear here within a few minutes of arriving.'}</td></tr>
              ) : leads.items.map((lead) => {
                const sla = slaState(lead, now);
                return (
                  <tr key={lead.id} className={`routing-row${selected?.id === lead.id ? ' active' : ''}`} onClick={() => openLead(lead.id)}>
                    <td data-label="Enquiry">{formatIst(lead.enquiry_at)}</td>
                    <td data-label="Customer"><strong>{lead.name || '—'}</strong><br /><small>{lead.phone}</small></td>
                    <td data-label="City">{lead.city || lead.city_raw || '—'}</td>
                    <td data-label="Zone">{zones[lead.zone] || lead.zone}</td>
                    <td data-label="Assigned to">{lead.assigned_to_name || <em>Unassigned</em>}</td>
                    <td data-label="Source" className="col-extra">{lead.source}</td>
                    <td data-label="Status"><span className={`campaign-status-badge status-${(lead.status || '').toLowerCase().replace(/\s+/g, '-')}`}>{lead.status}</span></td>
                    <td data-label="Follow-up">{sla ? <span className={`routing-chip ${sla.tone}`}>{sla.label}</span> : <span className="routing-ok">—</span>}</td>
                    {user?.role === 'admin' && <td data-label="Sheet" className="col-extra"><small>{sheetLabel(lead)}</small></td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="routing-pager">
            <span>{leads.total} lead{leads.total === 1 ? '' : 's'}</span>
            <button type="button" className="campaign-secondary-btn" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</button>
            <span>Page {page + 1} of {pages}</span>
            <button type="button" className="campaign-secondary-btn" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>Next</button>
          </div>
        </div>

        {selected && (
          <LeadPanel
            key={selected.id + selected.updated_at}
            apiUrl={apiUrl}
            lead={selected}
            roster={roster}
            user={user}
            statuses={statuses}
            onClose={closePanel}
            onChanged={handleChanged}
          />
        )}
      </div>
    </div>
  );
}
